/**
 * Surviving a failed chunk fetch.
 *
 * ═══ Why this exists at all ═══════════════════════════════════════════════════
 *
 * The sixteen screens are route-split, which was the fix for a 516 kB entry bundle. Splitting
 * introduces a failure mode that a single bundle simply does not have: the page's JavaScript is
 * fetched LATER, over the network, from a URL that contains a content hash.
 *
 * So the ordinary deploy becomes a user-visible crash. Someone has the console open, we ship,
 * `OverviewPage-PP4CBkKr.js` is replaced by a new hash, they click Templates — and the request for a
 * file that no longer exists rejects. `React.lazy` turns that rejection into a render-time throw that
 * unmounts the tree to the nearest error boundary, and this application had none, so the result was a
 * blank white page.
 *
 * The AI console learned this the expensive way. Its boundary carries the note that a customer ended
 * up reading "Failed to fetch dynamically imported module" and an asset filename — a message that
 * names our build output, tells the reader nothing they can act on, and makes a routine deploy look
 * like a crash. That mistake is not repeated here: the technical string never reaches the screen.
 *
 * ═══ Why not import the AI console's version ══════════════════════════════════
 *
 * Decision 0005 makes these separate applications that share no components, and a test enforces that
 * they do not import each other. Their implementation is also larger than this one needs, because it
 * compares against a deployed build manifest this product does not publish.
 *
 * ═══ The order of operations, and why it is that order ════════════════════════
 *
 *   1. Try the import.
 *   2. If it failed for a reason that is NOT a chunk-loading problem, rethrow untouched. A
 *      `SyntaxError` from the module's own top-level code is a real bug, and dressing it up as
 *      "please refresh" would hide it forever while telling the user something false.
 *   3. Retry once after a short pause. Most chunk failures are transient — a tunnel, a dropped frame,
 *      a proxy hiccup — and a quiet retry costs the user a loading state instead of their place in
 *      the page.
 *   4. If the retry also fails, the file is probably genuinely gone (we shipped). Reload once, so the
 *      browser fetches the new index and its new hashes.
 *   5. If we have ALREADY reloaded for this reason, stop and let the boundary explain. Reloading
 *      again would be an infinite loop, which is worse than an error screen because the user cannot
 *      even read it.
 *
 * Step 5 is the one that has to be right. The guard lives in `sessionStorage`, so it is per-tab and
 * clears itself when the tab closes — a `localStorage` guard would make a single bad deploy
 * permanently un-retryable on that machine even after we fixed it.
 */

/** Pause before the retry: long enough for a blip to pass, short enough to still read as loading. */
const RETRY_DELAY_MS = 600;

const RELOAD_GUARD_KEY = "pa-email-chunk-reload";

/** Tagged so the boundary can tell "we shipped while you were reading" from a genuine fault. */
export class AppUpdateRequiredError extends Error {
  readonly isAppUpdateRequired = true;
  constructor() {
    // Deliberately human. This string may be shown; see the boundary.
    super("A newer version of this console is available.");
    this.name = "AppUpdateRequiredError";
  }
}

export function isAppUpdateRequiredError(err: unknown): boolean {
  return Boolean(err && typeof err === "object" && (err as AppUpdateRequiredError).isAppUpdateRequired);
}

/**
 * Is this the browser failing to fetch or evaluate a JavaScript module?
 *
 * Matched on message text because there is no error TYPE for it: browsers disagree on both the class
 * and the wording. Chrome throws a TypeError "Failed to fetch dynamically imported module", Firefox
 * "error loading dynamically imported module", Safari "Importing a module script failed". Matching
 * loosely across them is the point — a miss here means a real bug gets a "please refresh" screen, or
 * a deploy gets an error screen.
 */
export function isChunkLoadFailure(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return /dynamically imported module|importing a module script failed|error loading dynamically|failed to fetch dynamically|ChunkLoadError|Loading chunk \d+ failed/i.test(
    message,
  );
}

function hasAlreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    // Private mode with storage denied. Treated as "already reloaded" — refusing to reload shows an
    // error screen the user can act on, whereas guessing "no" risks a reload loop we cannot break.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    /* Storage denied; hasAlreadyReloaded() fails closed above. */
  }
}

/**
 * Clear the guard once the app has successfully started.
 *
 * Without this, one recovered deploy would leave the tab unable to recover from the NEXT one, because
 * the flag would still be set from an hour ago. Called from App once it has rendered, which is the
 * only honest signal that whatever we reloaded for is now working.
 */
export function clearChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* nothing to clear */
  }
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * `React.lazy`'s factory, wrapped so one failed request is not a crash.
 *
 * Returns the factory rather than the lazy component so the call sites in App.tsx keep reading as
 * `lazy(...)` — the split is the thing worth seeing there, not this recovery.
 */
export function withChunkRetry<T>(factory: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      return await factory();
    } catch (err) {
      // A real error in the module's own code. Must not be disguised.
      if (!isChunkLoadFailure(err)) throw err;

      await delay(RETRY_DELAY_MS);
      try {
        return await factory();
      } catch (secondErr) {
        if (!isChunkLoadFailure(secondErr)) throw secondErr;

        if (!hasAlreadyReloaded()) {
          markReloaded();
          window.location.reload();
          /*
           * Hold the promise open. Resolving or rejecting here would render either the page or an
           * error screen for the fraction of a second before navigation commits, and a screen that
           * flashes and vanishes is worse than no screen.
           */
          return await new Promise<T>(() => {});
        }

        throw new AppUpdateRequiredError();
      }
    }
  };
}
